import { Stack } from '@mui/material'
import { RegModalWrapper } from '../ModalContent'
import styles from '../index.module.scss'
import { useMemo, useState } from 'react'
import pxToRem from '../../../../helpers/pxToRem'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { UserRegSx } from '../../../../components/TextField'
import CountrySelect from '../../../../components/AutoComplete/CountryInput'
import { documentUploadHandler, primaryIdentity } from './helper'
import FileUpload, { FileType } from '../../../../components/FileUpload'
import { setErrorMsg } from '../../../../redux/uiSlice'
import { RegFileUploadLabel } from '../../../../components/FileUpload/RegUploadComponents'
import {
    IdentificationDocument,
    identityDocumentTypes,
    WorkingRightsType,
} from '../../../../schemas/teacherReg'
import { selectUserUid } from '../../../../redux/userSlice'
import {
    selectTeacherPrimaryIdentity,
    selectTeacherSecondaryIdentity,
    selectWorkingRightsType,
} from '../../../../redux/teacherSlice'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import { DayjsSchema, FileUploadSchema } from '../../../../schemas/helper'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { db } from '../../../../firebase/configs'
import { push, ref, update } from 'firebase/database'
import z from 'zod'
import FormSelect from '../../../../components/FormComponents/FormSelect'
import FormInputText from '../../../../components/FormComponents/FormInputText'
import FormDatePicker_v2 from '../../../../components/FormComponents/FormDatePicker_v2'

export const identityFormSchema = z.object({
    documentType: identityDocumentTypes,
    reference: z.string().min(1),
    country: z.string().min(1),
    name: z.string().min(1),
    birthDate: DayjsSchema.refine(
        (v) => v.isBefore(dayjs()),
        'Please select a date before today'
    ),
    document: FileUploadSchema,
})

export type IdentityFormType = z.infer<typeof identityFormSchema>

export const PrimaryIdentityWrapper = () => {
    const document = useSelector(selectTeacherPrimaryIdentity)
    const secondaryDoc = useSelector(selectTeacherSecondaryIdentity)
    const workingRightsType = useSelector(selectWorkingRightsType)

    const updatedSchema = useMemo(
        () =>
            secondaryDoc.loading
                ? undefined
                : identityFormSchema.check((ctx) => {
                      if (
                          typeof secondaryDoc.document.data?.birthDate ===
                              'string' &&
                          ctx.value.birthDate.format('DD-MM-YYYY') !==
                              secondaryDoc.document.data.birthDate
                      ) {
                          ctx.issues.push({
                              code: 'custom',
                              message:
                                  'This birthdate does not match the other identification document',
                              input: ctx.value.birthDate,
                              path: ['birthDate'],
                          })
                      }
                  }),
        [secondaryDoc]
    )

    if (
        document.loading ||
        workingRightsType.loading ||
        secondaryDoc.loading ||
        updatedSchema === undefined
    )
        return <LoadingSpinner />

    return (
        <PrimaryIdentityForm
            id={document.document.id}
            data={document.document.data}
            secondaryDoc={secondaryDoc.document.data}
            workingRightsType={workingRightsType.data}
            schema={updatedSchema}
        />
    )
}

const PrimaryIdentityForm = ({
    id,
    data,
    workingRightsType,
    secondaryDoc,
    schema,
}: {
    id: string | undefined
    data: IdentificationDocument
    secondaryDoc: IdentificationDocument | null
    workingRightsType: WorkingRightsType | null
    schema: typeof identityFormSchema
}) => {
    const dispatch = useDispatch()
    const userId = useSelector(selectUserUid)

    const {
        formState: { isDirty, isValid },
        setValue,
        control,
        handleSubmit,
        resetField,
        watch,
    } = useForm<IdentityFormType>({
        resolver: zodResolver(schema),
        mode: 'onChange',
        defaultValues: {
            documentType: data.documentType || undefined,
            country: data.country || '',
            name: data.name || '',
            reference: data.reference || '',
            birthDate: data.birthDate
                ? dayjs(data.birthDate, 'DD-MM-YYYY')
                : undefined,
            document: data.value || undefined,
        },
    })
    const selectedDocType = watch('documentType')

    const [file, setFile] = useState<FileType | undefined>(
        !!data.value ? { file: data.value } : undefined
    )
    const onFileChange = (file: any) => {
        let f =
            file instanceof File
                ? file
                : new File([file], file.name, { type: file.type })

        setFile({ file: file, fileName: file?.name })
        setValue('document', f, {
            shouldDirty: true,
            shouldValidate: true,
        })
    }

    const handleUpdate = async (submission: IdentityFormType) => {
        try {
            if (!userId) {
                throw new Error(
                    'User is not authenticated. Please refresh and try again.'
                )
            }

            if (!isDirty) return

            const { fileUrl, fileType, docTitle } = await documentUploadHandler(
                userId,
                file,
                {
                    url: data.value,
                    type: data.type,
                    docTitle: data.docTitle,
                }
            ) // TODO: Add type and name here

            let toUpdate = {
                ...submission,

                // Do not push File object into update
                document: null,
                value: fileUrl,
                type: fileType,
                docTitle: docTitle,

                birthDate: submission.birthDate.format('DD-MM-YYYY'),
                state: 'submitted',
            }

            if (typeof id === 'string') {
                // Update data
                await update(
                    ref(db, `teachers/${userId}/identity/${id}`),
                    toUpdate
                )
            } else {
                await push(ref(db, `teachers/${userId}/identity`), toUpdate)
            }
        } catch (error: any) {
            dispatch(setErrorMsg(error.message))
        }
    }

    const removeFile = () => {
        setFile(undefined)
        resetField('document')
    }

    const options = useMemo(() => {
        return primaryIdentity
            .filter((el) => el.value !== secondaryDoc?.type)
            .sort((a, b) => a.value.localeCompare(b.value))
    }, [secondaryDoc?.type])

    return (
        <>
            <RegModalWrapper
                title="Identity Verification - Primary Document"
                handleUpdate={handleSubmit(handleUpdate)}
                state={data.state}
                isAllComplete={isValid}
            >
                <Stack gap={pxToRem(40)} className={styles.modalContainer}>
                    <Stack className={styles.titleContainer}>
                        <h5 className={styles.h5}>Primary Identification</h5>
                        <p className={styles.p}>
                            To identify your identity and working rights you
                            will need to upload two forms of identification
                        </p>
                    </Stack>
                    <Stack gap={pxToRem(24)}>
                        <FormSelect
                            control={control}
                            name="documentType"
                            options={
                                workingRightsType === 'temporaryVisaHolder'
                                    ? [primaryIdentity[0]]
                                    : options
                            }
                            controlProps={{
                                sx: UserRegSx,
                            }}
                            props={{
                                label: 'Document Type',
                            }}
                        />
                        {!!selectedDocType ? (
                            <>
                                <Controller
                                    render={(props) => (
                                        <CountrySelect
                                            value={props.field.value}
                                            setValue={props.field.onChange}
                                            error={!!props.fieldState.error}
                                            helperText={
                                                props.fieldState.error?.message
                                            }
                                        />
                                    )}
                                    name={'country'}
                                    control={control}
                                />
                                <FormInputText
                                    control={control}
                                    name="name"
                                    label="Name on Document"
                                    sx={UserRegSx}
                                />
                                <Stack direction={'row'} gap={pxToRem(24)}>
                                    <FormDatePicker_v2
                                        control={control}
                                        name="birthDate"
                                        datePickerProps={{
                                            label: 'Date of Birth',
                                            format: 'DD-MM-YYYY',
                                            maxDate: dayjs(),
                                            sx: UserRegSx,
                                        }}
                                    />
                                    <FormInputText
                                        control={control}
                                        name="reference"
                                        label="Document Number"
                                        sx={UserRegSx}
                                    />
                                </Stack>
                                <RegFileUploadLabel
                                    file={file}
                                    reset={removeFile}
                                />
                                <FileUpload
                                    file={file}
                                    onChangeCallback={onFileChange}
                                    extensions={'document'}
                                    iconColor={styles.primaryYellow500}
                                />
                            </>
                        ) : null}
                    </Stack>
                </Stack>
            </RegModalWrapper>
        </>
    )
}

export default PrimaryIdentityWrapper
